'use client';

import { type ComponentProps, memo } from 'react';
import { useTrack } from '@frameio/shared/segment/hooks';
import { useCreateShare } from '@frameio/shared/shares/hooks';
import { IconButton2, Tooltip } from '@frameio/vapor';
import { IconPlus } from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/CreateShareButton';

TEXT.DEFINE({
  /** Tooltip and aria-label for creating a share */
  createShare: 'Create Share Link',
});

type Props = {
  buttonVariant?: ComponentProps<typeof IconButton2>['variant'];
  projectId: string;
};

export const CreateShareButton = memo(
  ({ buttonVariant = 'tertiary', projectId }: Props) => {
    const track = useTrack();

    const { createShareFromAssets } = useCreateShare();

    async function createShare() {
      const share = await createShareFromAssets({
        assetIds: [],
        projectId,
      });

      if (!share) {
        return;
      }

      track('static-share-created', {
        num_items_selected: 0,
        project_id: projectId,
        source: 'ProjectNavButton',
        is_share_url_copied_automatically: false,
        share_id: share.data?.createShareFromAssets?.share?.id,
      });
    }

    return (
      <Tooltip placement="top" title={TEXT.createShare}>
        <IconButton2
          data-dd-action-name="share-builder.create-share"
          icon={<IconPlus />}
          label={TEXT.createShare}
          // eslint-disable-next-line react/jsx-no-bind
          onPress={createShare}
          size="small"
          variant={buttonVariant}
        />
      </Tooltip>
    );
  }
);
